import { useCallback, useEffect, useRef, useState } from 'react';
import dropin from 'braintree-web-drop-in';
import type {
  Dropin,
  applePayCreateOptions,
  googlePayCreateOptions,
} from 'braintree-web-drop-in';
import * as Sentry from '@sentry/react';
import useGetBraintreeToken from './useGetBraintreeToken';

export const BRAINTREE_DROPIN_CONTAINER_ID = 'braintree-drop-in';

const MERCHANT_LABEL = 'Kingdom Strollers';

type ApplePayOptions = Omit<applePayCreateOptions, 'paymentRequest'> & {
  paymentRequest: Pick<applePayCreateOptions['paymentRequest'], 'total'>;
};

function applePayConfig(totalPrice: string): ApplePayOptions {
  return {
    displayName: MERCHANT_LABEL,
    paymentRequest: {
      total: { label: MERCHANT_LABEL, amount: totalPrice },
    },
  };
}

type GooglePayOptions = Omit<googlePayCreateOptions, 'merchantId'> &
  Partial<Pick<googlePayCreateOptions, 'merchantId'>>;

function googlePayConfig(totalPrice: string): GooglePayOptions {
  const merchantId = process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID?.trim();

  return {
    googlePayVersion: 2,
    ...(merchantId && { merchantId }),
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPrice,
      currencyCode: 'USD',
    },
  };
}

export type BraintreePaymentStatus = 'waiting' | 'creating' | 'ready' | 'error';

interface UseBraintreePaymentOptions {
  amount: number | undefined;
  isEdit: boolean;
}

interface BraintreePayment {
  status: BraintreePaymentStatus;
  requestPaymentNonce: () => Promise<string>;
  clearSelectedPaymentMethod: () => void;
}

async function teardownInstance(instance: Dropin): Promise<void> {
  try {
    await instance.teardown();
  } catch (error) {
    Sentry.captureException(error);
  }
}

function useBraintreePayment({
  amount,
  isEdit,
}: UseBraintreePaymentOptions): BraintreePayment {
  const { data: braintreeData, error: tokenError } =
    useGetBraintreeToken(isEdit);
  const token = braintreeData?.ClientAuthorization;
  const totalPrice = amount != null ? amount.toFixed(2) : undefined;
  const canCreate = !!token && totalPrice !== undefined;

  const instanceRef = useRef<Dropin>();
  const lifecycleRef = useRef<Promise<void>>(Promise.resolve());
  const appliedPriceRef = useRef<string>();
  const [widgetStatus, setWidgetStatus] =
    useState<BraintreePaymentStatus>('waiting');

  useEffect(() => {
    if (!token || totalPrice === undefined) {
      setWidgetStatus('waiting');
      return;
    }

    let cancelled = false;
    setWidgetStatus('creating');

    lifecycleRef.current = lifecycleRef.current.then(async () => {
      if (cancelled) return;

      try {
        const instance = await dropin.create({
          authorization: token,
          container: `#${BRAINTREE_DROPIN_CONTAINER_ID}`,
          paypal: {
            flow: 'vault',
          },
          // Braintree merges the gateway's country, currency, networks, and
          // capabilities into this request.
          applePay: applePayConfig(totalPrice) as applePayCreateOptions,
          googlePay: googlePayConfig(totalPrice) as googlePayCreateOptions,
        });

        if (cancelled) {
          await teardownInstance(instance);
          return;
        }

        appliedPriceRef.current = totalPrice;
        instanceRef.current = instance;
        setWidgetStatus('ready');
      } catch (error) {
        if (!cancelled) {
          Sentry.captureException(error);
          setWidgetStatus('error');
        }
      }
    });

    return () => {
      cancelled = true;
      const instance = instanceRef.current;
      instanceRef.current = undefined;
      appliedPriceRef.current = undefined;

      if (instance) {
        lifecycleRef.current = lifecycleRef.current.then(() =>
          teardownInstance(instance),
        );
      }
    };
    // Quote changes are updated in place so entered card details survive.
  }, [canCreate, token]);

  useEffect(() => {
    const instance = instanceRef.current;
    if (
      widgetStatus !== 'ready' ||
      !instance ||
      totalPrice === undefined ||
      totalPrice === appliedPriceRef.current
    ) {
      return;
    }

    try {
      instance.updateConfiguration(
        'applePay',
        'paymentRequest',
        applePayConfig(totalPrice).paymentRequest,
      );
      instance.updateConfiguration(
        'googlePay',
        'transactionInfo',
        googlePayConfig(totalPrice).transactionInfo,
      );
      appliedPriceRef.current = totalPrice;
    } catch (error) {
      Sentry.captureException(error);
      setWidgetStatus('error');
    }
  }, [totalPrice, widgetStatus]);

  const requestPaymentNonce = useCallback(async () => {
    const instance = instanceRef.current;
    if (!instance) throw new Error('Payment form is not ready.');

    const payload = await instance.requestPaymentMethod();
    return payload.nonce;
  }, []);

  const clearSelectedPaymentMethod = useCallback(() => {
    instanceRef.current?.clearSelectedPaymentMethod();
  }, []);

  return {
    status: tokenError ? 'error' : widgetStatus,
    requestPaymentNonce,
    clearSelectedPaymentMethod,
  };
}

export default useBraintreePayment;
